import { t } from '@ankr.com/common';
import {
  ChainID,
  Chain,
  ChainURL,
  ChainType,
  ChainSubType,
} from '@ankr.com/chains-list';

import { EndpointGroup } from 'modules/endpoints/types';
import { IChainParams } from 'modules/wallet/actions/addNetwork';
import { NETWORK_INFO_MAP } from 'modules/wallet/constants';
import { flatNetworkURLs } from 'modules/wallet/utils/flatNetworkURLs';
import { getChainId } from 'modules/chains/utils/getChainId';

import { getChainById } from './getChainById';

const addNamePostfix = (name: string) =>
  t('chain-item.get-started.endpoints.network-postfix', { name });

const flattenAllChainTypes = (chain: Chain): Chain[] => [
  chain,
  ...(chain.extenders || []).flatMap(flattenAllChainTypes),
  ...(chain.extensions || []).flatMap(flattenAllChainTypes),
  ...(chain.testnets || []).flatMap(flattenAllChainTypes),
  ...(chain.devnets || []).flatMap(flattenAllChainTypes),
  ...(chain.signets || []).flatMap(flattenAllChainTypes),
];

const getMappedNetwork = (
  isEnterprise?: boolean,
  chain?: Chain,
  flatChainId?: ChainID,
): IChainParams | undefined => {
  const networkInfo = NETWORK_INFO_MAP[flatChainId || ChainID.UNDEFINED];

  if (!networkInfo || !chain) {
    return undefined;
  }

  const rpcUrls = flatNetworkURLs<ChainURL, Chain>(chain).mainnetURLs.map(
    ({ enterprise, rpc }) => (isEnterprise ? enterprise : rpc),
  );

  return {
    ...networkInfo,
    id: networkInfo.id,
    name: addNamePostfix(networkInfo.name),

    rpcUrls: {
      default: {
        http: rpcUrls,
      },
    },
  };
};

interface GetFlattenChainArguments {
  publicChain: Chain;
  chainType: ChainType;
  group: EndpointGroup;
  chainSubType?: ChainSubType;
}

const getFlattenChain = ({
  chainSubType,
  chainType,
  group,
  publicChain,
}: GetFlattenChainArguments) => {
  const flatChainId = getChainId({
    publicChain,
    chainType,
    chainSubType,
    group,
    withExceptions: false,
    keepEVMChainID:
      publicChain.id === ChainID.ZETACHAIN ||
      publicChain.id === ChainID.AVALANCHE_EVM ||
      publicChain.id === ChainID.AVALANCHE_FUJI_EVM ||
      publicChain.id === ChainID.KAVA_EVM ||
      publicChain.id === ChainID.TENET_EVM ||
      publicChain.id === ChainID.HORIZEN_EVM ||
      publicChain.id === ChainID.KAVA ||
      publicChain.id === ChainID.TAC ||
      publicChain.id === ChainID.TAC_EVM ||
      publicChain.id === ChainID.IOTA_EVM ||
      publicChain.id === ChainID.ZERO_G ||
      publicChain.id === ChainID.SEI,
  }) as ChainID;

  const flatChains = flattenAllChainTypes(publicChain);

  const flatChain = getChainById(flatChains, flatChainId);

  return { flatChainId, flatChain };
};

interface GetNetworkConfigurationProps {
  isEnterprise: boolean;
  publicChain: Chain;
}

const getNetworkConfiguration = ({
  isEnterprise,
  publicChain,
}: GetNetworkConfigurationProps) => {
  return getMappedNetwork(
    isEnterprise,
    publicChain,
    publicChain?.id as ChainID,
  );
};

interface GetNetworkParamsParams {
  chain: Chain;
  chainType?: ChainType;
  chainSubType?: ChainSubType;
  group?: EndpointGroup;
  isEnterprise?: boolean;
}

const getFlattenChainAndId = ({
  chain,
  chainSubType,
  chainType,
  group,
}: GetNetworkParamsParams) => {
  if (group && chainType) {
    return getFlattenChain({
      publicChain: chain,
      chainType,
      group,
      chainSubType,
    });
  }

  return { flatChain: chain };
};

export const getNetworkParams = ({
  chain,
  chainSubType,
  chainType,
  group,
  isEnterprise = false,
}: GetNetworkParamsParams) => {
  const { flatChain } = getFlattenChainAndId({
    chain,
    chainType,
    chainSubType,
    group,
  });

  const networkConfiguration = getNetworkConfiguration({
    isEnterprise,
    publicChain: flatChain!,
  });

  return {
    networkConfiguration,
  };
};
